import React, { useEffect, useState } from "react";
import { apiGet } from "../../services/api";
import ChartsRangeSelector, { RangeInterval } from "../common/apexo/ChartsRangeSelector";
import "./ManagerDaySummary.css";

interface BillRow {
  net?: number;
  patient?: string;
}

interface PaymentRow {
  amount?: number;
  patient?: string;
  mode?: string;
}

interface InvoiceRow {
  amount?: number;
  patient?: string;
}

interface ManagerDaySummaryProps {
  date: string;
  onClose: () => void;
}


interface ExpenseRow {
  id: number;
  source: string;
  description: string;
  amount: number;
}

export default function ManagerDaySummary({ date, onClose }: ManagerDaySummaryProps) {
  const [data, setData] = useState<{
    bills: BillRow[];
    payments: PaymentRow[];
    invoices: InvoiceRow[];
    expenses: ExpenseRow[];
    totals?: {
      billed_total?: number;
      collected_total?: number;
      cash_total?: number;
      upi_total?: number;
      card_total?: number;
      bank_total?: number;
      other_total?: number;
      expenses_total?: number;
      net_cash_in_hand?: number;
    };
  }>({
    bills: [],
    payments: [],
    invoices: [],
    expenses: [],
    totals: {}
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [rangeStart, setRangeStart] = useState(() => {
    return date ? new Date(date) : new Date();
  });
  const [rangeEnd, setRangeEnd] = useState(() => {
    return date ? new Date(date) : new Date();
  });
  const [interval, setInterval] = useState<RangeInterval>('day');

  // Derive targetDate for fetching from rangeStart (since backend only supports one date currently per the prompt "Keep existing sources")
  const targetDateString = rangeStart.toISOString().split('T')[0];

  useEffect(() => {
    if (!date) return;

    setLoading(true);
    setError(null);

    apiGet(`/api/manager/day-summary?date=${date}`)
      .then((value: unknown) => {
        const res = value as {
          bills?: BillRow[];
          payments?: PaymentRow[];
          invoices?: InvoiceRow[];
          expenses?: ExpenseRow[];
          totals?: Record<string, number>
        };
        setData({
          bills: Array.isArray(res.bills) ? res.bills : [],
          payments: Array.isArray(res.payments) ? res.payments : [],
          invoices: Array.isArray(res.invoices) ? res.invoices : [],
          expenses: Array.isArray(res.expenses) ? res.expenses : [],
          totals: res.totals || {}
        });
      })
      .catch((err) => {
        console.error("Day summary error:", err);
        setError("Failed to load daily summary.");
      })
      .finally(() => setLoading(false));
  }, [targetDateString]);

  const totalBilled = data.totals?.billed_total ?? data.bills.reduce(
    (sum, b) => sum + Number(b.net || 0),
    0
  );

  const totalCollected = data.totals?.collected_total ?? data.payments.reduce(
    (sum, p) => sum + Number(p.amount || 0),
    0
  );

  const totalEstimates = data.invoices.reduce(
    (sum, i) => sum + Number(i.amount || 0),
    0
  );

  return (
    <div className="summary-overlay">
      <div className="summary-modal card">

        <div className="modal-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>📊 Daily Summary</h3>
            <button className="btn-close" type="button" onClick={onClose}>×</button>
          </div>
          <div style={{ background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <ChartsRangeSelector 
              startDate={rangeStart}
              endDate={rangeEnd}
              interval={interval}
              onRangeChange={(s, e) => { setRangeStart(s); setRangeEnd(e); }}
              onIntervalChange={setInterval}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-state">Loading summary…</div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : (
          <div className="modal-body">

            <section className="report-section">
              <h4>🩺 Clinical Revenue</h4>

              <table className="report-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th className="text-right">Net Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.bills.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="muted">
                        No clinical bills generated.
                      </td>
                    </tr>
                  ) : (
                    data.bills.map((b, i) => (
                      <tr key={i}>
                        <td>{b.patient}</td>
                        <td className="text-right">
                          ₹{Number(b.net).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Total Billed</strong></td>
                    <td className="text-right">
                      <strong>₹{totalBilled.toLocaleString()}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </section>

            <section className="report-section">
              <h4>💰 Payments Received</h4>

              <table className="report-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Mode</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="muted">
                        No payments recorded.
                      </td>
                    </tr>
                  ) : (
                    data.payments.map((p, i) => (
                      <tr key={i}>
                        <td>{p.patient}</td>
                        <td>
                          <span className="mode-badge">
                            {p.mode || "—"}
                          </span>
                        </td>
                        <td className="text-right">
                          ₹{Number(p.amount).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}><strong>Total Collected</strong></td>
                    <td className="text-right success-text">
                      <strong>₹{totalCollected.toLocaleString()}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>

              <div className="mode-split">
                <div className="mode-item">Cash: ₹{Number(data.totals?.cash_total || 0).toLocaleString()}</div>
                <div className="mode-item">UPI: ₹{Number(data.totals?.upi_total || 0).toLocaleString()}</div>
                <div className="mode-item">Card: ₹{Number(data.totals?.card_total || 0).toLocaleString()}</div>
                <div className="mode-item">Bank: ₹{Number(data.totals?.bank_total || 0).toLocaleString()}</div>
                <div className="mode-item">Other: ₹{Number(data.totals?.other_total || 0).toLocaleString()}</div>
              </div>
            </section>

            {data.invoices.length > 0 && (
              <section className="report-section">
                <h4>📄 Estimates / Quotes</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th className="text-right">Estimated Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.invoices.map((inv, i) => (
                      <tr key={i}>
                        <td>{inv.patient}</td>
                        <td className="text-right">
                          ₹{Number(inv.amount).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td><strong>Pipeline Value</strong></td>
                      <td className="text-right">
                        <strong>₹{totalEstimates.toLocaleString()}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </section>
            )}

            {/* Expenses Section */}
            {data.expenses && data.expenses.length > 0 && (
              <section className="report-section">
                <h4>💸 Expenses & Outflows</h4>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Description</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expenses.map((exp: ExpenseRow, i: number) => (
                      <tr key={i}>
                        <td>{exp.source}</td>
                        <td>{exp.description}</td>
                        <td className="text-right error-text">
                          - ₹{Number(exp.amount).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}><strong>Total Expenses</strong></td>
                      <td className="text-right error-text">
                        <strong>- ₹{Number(data.totals?.expenses_total || 0).toLocaleString()}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </section>
            )}

            <div className="net-cash-card">
              <div className="net-cash-label">Net Cash in Hand</div>
              <div className="net-cash-value">
                ₹{Number(data.totals?.net_cash_in_hand || (totalCollected - Number(data.totals?.expenses_total || 0))).toLocaleString()}
              </div>
              <div className="net-cash-sub">
                (Collected - Expenses)
              </div>
            </div>

          </div>
        )}

        <div className="modal-footer">
          <button className="btn-print" type="button" onClick={() => window.print()}>
            🖨️ Print
          </button>
        </div>

      </div>
    </div>
  );
}
